/* istanbul ignore file */
import { getIn } from 'seamless-immutable';
import config from '@catalogo/core-configuration/config/out/config.server.json';
import { formatPrice } from '@catalogo/core-helpers/price';

export const getSuggestedPrice = (offer, mainPayment) => {
  const suggestedPrice = offer?.suggestedPrice;
  const { listPrice, price } = mainPayment;

  if (suggestedPrice > price && suggestedPrice > listPrice) {
    return formatPrice(suggestedPrice);
  }

  return 0;
};

export const getBuyTogetherPrice = (data, salesPrice) => {
  const buyTogetherVipEnabled = data?.buyTogetherVIP?.active;
  const buyTogetherVipPrice = data?.buyTogetherVIP?.price;

  return buyTogetherVipEnabled ? `R$ ${formatPrice(salesPrice + buyTogetherVipPrice)}` : null;
};

const fmtSupercashbackText = cashback => `super cashback • ${cashback.rate}% de volta`;

const fmtCashbackText = ({ cashback }) => {
  const cashbackText = `ganhe <strong>R$ ${formatPrice(cashback.value)}</strong> de volta pagando com Ame`;
  return cashback.value > 0 ? cashbackText : null;
};

const hasSuperCashback = (cashback, config) => cashback.rate >= config.rate || cashback.value >= config.value;

export const getCashbackFromBestPayment = (offer, bestPaymentType, superCashbackConfig) => {
  const payment = offer?.[bestPaymentType];
  const cashback = payment?.cashback;
  const cashbackInCash = payment?.minQuantity?.[0]?.cashback;

  if (!cashback || !cashbackInCash) {
    return;
  }

  if (cashback.value === cashbackInCash.value) {
    const supercashback = hasSuperCashback(cashback, superCashbackConfig);
    const supercashbackText = supercashback && fmtSupercashbackText(cashback);

    return { supercashbackText, cashbacks: [fmtCashbackText({ cashback })] };
  }

  const cashbacks = [
    { cashback, superCashback: hasSuperCashback(cashback, superCashbackConfig) },
    {
      cashback: cashbackInCash,
      superCashback: hasSuperCashback(cashbackInCash, superCashbackConfig),
    },
  ].sort((a, b) => b.cashback - a.cashback);

  const supercashbackText = cashbacks[0].superCashback && fmtSupercashbackText(cashbacks[0].cashback);
  const formatedCashbacks = cashbacks.map(fmtCashbackText);

  return { supercashbackText, cashbacks: formatedCashbacks };
};

const getCashback = ({ offer, superCashbackConfig }) => {
  if (offer?.ame?.cashback?.value > 0) {
    return getCashbackFromBestPayment(offer, 'ame', superCashbackConfig);
  }
};

const getPrice = (offer, card) => {
  if (getIn(card, ['minQuantity', '0', 'discount', 'value']) > 0) {
    return {
      price: getIn(card, ['minQuantity', '0', 'value']),
      listPrice: Math.max(card.price, offer.salesPrice),
    };
  }

  if (getIn(card, ['discount', 'value']) > 0) {
    return {
      price: card.price,
      listPrice: offer.salesPrice,
    };
  }

  return {
    price: offer.salesPrice,
    listPrice: offer.listPrice,
  };
};

const getDiscount = (type, payment, offer) => {
  const inCashDiscount = payment?.minQuantity?.[0]?.discount ?? {};
  const paymentDiscount = payment?.discount ?? {};
  const offerDiscount = offer?.discount ?? {};

  let discount = offerDiscount;
  if (type === 'boleto' || type === 'pix') {
    discount = paymentDiscount;
  } else if (inCashDiscount.value > 0) {
    discount = inCashDiscount;
  } else if (paymentDiscount.value > 0) {
    discount = paymentDiscount;
  }

  const { rate } = discount;
  return { ...discount, rateText: rate >= 5 ? `${rate}%` : 'baixou' };
};

const getPaymentDisplay = type => {
  switch (type) {
    case 'cartaoVisa':
      return 'à vista no cartão de crédito';
    case 'cartaoMarca':
      return `à vista no cartão de crédito`;
    case 'boleto':
      return 'no boleto';
    case 'pix':
      return 'no Pix';
  }
};

const getConditionText = (bestPaymentType, hasInCashDiscount) => {
  if (['boleto', 'pix'].includes(bestPaymentType) || hasInCashDiscount) {
    return getPaymentDisplay(bestPaymentType);
  }
};

export const hasInCashDiscount = payment => {
  const hasInCashDiscount = payment?.minQuantity?.[0]?.discount?.value > 0;
  const inCashPrice = payment?.minQuantity?.[0]?.value;
  const maxPriceWithoutInterest = payment?.maxQuantity?.[0]?.total;
  const maxParcelsWithoutInterest = payment?.maxQuantity?.[0]?.quantity;
  const notIsPixOrBoleto = !['pix', 'boleto'].includes(payment?.type?.toLocaleLowerCase());

  const hasDiscount =
    maxParcelsWithoutInterest > 1 && inCashPrice === maxPriceWithoutInterest ? false : hasInCashDiscount;

  return notIsPixOrBoleto && hasDiscount;
};

const fmtInstallmentText = ({ payment, showPrice, showCardName = false }) => {
  const maxInstallmentWithoutInterest = payment?.maxQuantity?.[0];
  const total = maxInstallmentWithoutInterest?.total;
  const quantity = maxInstallmentWithoutInterest?.quantity;
  const value = maxInstallmentWithoutInterest?.value;

  if (quantity <= 1) {
    return;
  }

  const text = `até ${quantity}x de R$ ${formatPrice(value)}`;

  return showPrice ? `R$ ${formatPrice(total)} em ${text}` : text;
};

export const getInstallmentText = ({ offer, payment, inCashDiscount, bestPaymentType, isFinance }) => {
  if (['cartaoVisa', 'cartaoMarca'].includes(bestPaymentType)) {
    const isBrandCard = bestPaymentType === 'cartaoMarca';

    return fmtInstallmentText({ payment, showPrice: inCashDiscount, showCardName: isBrandCard });
  }

  const creditCard = offer?.cartaoVisa;

  if (isFinance) {
    const brandCard = offer?.cartaoMarca;
    const maxInstallmentBrandCard = brandCard?.maxQuantity?.[0]?.quantity;
    const maxInstallmentCreditCard = creditCard?.maxQuantity?.[0]?.quantity;
    const brandCardHasBetterInstallment = maxInstallmentBrandCard > maxInstallmentCreditCard;

    return fmtInstallmentText({
      payment: brandCardHasBetterInstallment ? brandCard : creditCard,
      showPrice: true,
      showCardName: brandCardHasBetterInstallment,
    });
  }

  return fmtInstallmentText({ payment: creditCard, showPrice: true });
};

export const formatPayment = ({ offer, bestPaymentType, isFinance, superCashbackConfig }) => {
  const payment = offer?.[bestPaymentType];
  const inCashDiscount = hasInCashDiscount(payment);
  const paymentConditionText = getConditionText(bestPaymentType, inCashDiscount);
  const paymentInstallmentText = getInstallmentText({ offer, payment, inCashDiscount, bestPaymentType, isFinance });
  const cashback = getCashback({ offer, superCashbackConfig });
  const discount = getDiscount(bestPaymentType, payment, offer);
  const { price, listPrice } = getPrice(offer, payment);

  return {
    type: bestPaymentType,
    paymentConditionText,
    paymentInstallmentText,
    priceText: price && `R$ ${formatPrice(price)}`,
    listPriceText: listPrice && `R$ ${formatPrice(listPrice)}`,
    cashback,
    discount,
    hasInCashDiscount: inCashDiscount,
  };
};

export const getType = type => {
  switch (type) {
    case 'CARTAO_VISA':
      return 'cartaoVisa';
    case config.brandCardId:
      return 'cartaoMarca';
    case 'BOLETO':
      return 'boleto';
    case 'PIX':
      return 'pix';
    default:
      return null;
  }
};

export const getPayments = ({ offer, isFinance, superCashbackConfig }) => {
  const bestPaymentType = getType(offer?.bestPaymentOption?.type);
  const mainPayment = formatPayment({ offer, bestPaymentType, isFinance, superCashbackConfig });

  return { mainPayment };
};

export const getPaymentOptionName = value => {
  switch (value) {
    case 'credit-card':
      return 'cartaoVisa';
    case 'ame':
      return 'ame';
    case 'brand-card':
      return 'cartaoMarca';
    case 'bank-splip':
      return 'boleto';
    case 'pix':
      return 'pix';
  }
};

const fmtInstallment = (installment, method) =>
  installment.map(install => {
    const interestRate = install?.interestRate;
    return {
      text: `${install?.quantity}x de R$ ${formatPrice(install?.value)}`,
      interestRateText: interestRate > 0 ? `com juros de ${formatPrice(interestRate)} % a.m.` : 'sem juros',
      interestRate: interestRate,
      total: `R$ ${formatPrice(install?.total)}`,
      key: `${method}-${install?.quantity}`,
      annualCET: install?.annualCET,
    };
  });

export const getInstallment = (offer, method) => {
  const installment = offer?.[method]?.installment ?? [];
  if (method === 'ame') {
    const cartaoVisa1x = offer?.cartaoVisa?.installment?.[0] ?? {};
    return fmtInstallment([cartaoVisa1x, ...installment.slice(1, installment.length)], method);
  }
  return fmtInstallment(installment, method);
};

export const getInterestRateDisclaimer = (installment, method) => {
  const { annualCET, interestRate } = installment.reduce(
    (total, { annualCET, interestRate }) => ({
      ...total,
      interestRate: Math.max(total.interestRate, interestRate),
      annualCET: Math.max(total.annualCET, annualCET),
    }),
    { annualCET: 0, interestRate: 0 }
  );

  const msgIOF = method !== 'cartaoMarca' ? '+ IOF a.m.' : '';

  const interestRateDisclaimer = `Para financiamento com juros de ${interestRate}% a.m. ${msgIOF} - CET máximo de ${annualCET}% a.a.`;

  return annualCET && interestRate ? interestRateDisclaimer : '';
};
